import { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Smartphone, Package, MapPin, Send, CheckCircle, Loader2, ExternalLink } from 'lucide-react';
import { apiFetch } from '../lib/api';

interface Illustration {
  id: number;
  title: string;
  imageUrl: string;
}

const PHONE_MODELS = [
  // iPhone
  'iPhone 16 Pro Max',
  'iPhone 16 Pro',
  'iPhone 16 Plus',
  'iPhone 16',
  'iPhone 15 Pro Max',
  'iPhone 15 Pro',
  'iPhone 15 Plus',
  'iPhone 15',
  'iPhone 14 Pro Max',
  'iPhone 14 Pro',
  'iPhone 14 Plus',
  'iPhone 14',
  'iPhone 13 Pro Max',
  'iPhone 13 Pro',
  'iPhone 13',
  // Samsung
  'Samsung Galaxy S25 Ultra',
  'Samsung Galaxy S25+',
  'Samsung Galaxy S25',
  'Samsung Galaxy S24 Ultra',
  'Samsung Galaxy S24+',
  'Samsung Galaxy S24',
  'Samsung Galaxy S23 Ultra',
  'Samsung Galaxy S23+',
  'Samsung Galaxy S23',
  // Google Pixel
  'Google Pixel 9 Pro XL',
  'Google Pixel 9 Pro',
  'Google Pixel 9',
  // Xiaomi
  'Xiaomi 15',
  'Xiaomi 14',
  // Other
  '其他（請自行輸入）',
];

const COLOR_OPTIONS = [
  { value: '曼波綠', label: '曼波綠' },
  { value: '灰粉', label: '灰粉' },
  { value: '古董白', label: '古董白' },
  { value: '莫蘭迪紫', label: '莫蘭迪紫' },
  { value: '雲青藍', label: '雲青藍' },
];

export default function PhoneCaseOrder() {
  const [searchParams] = useSearchParams();
  const donationNum = searchParams.get('dn') || '';

  const [illustrations, setIllustrations] = useState<Illustration[]>([]);
  const [form, setForm] = useState({
    donorName: '',
    phoneModel: '',
    customPhoneModel: '',
    illustrationChoice: '',
    colorPreference: '',
    sfLockerCode: '',
    sfLockerAddress: '',
  });
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [orderNum, setOrderNum] = useState('');
  const [alreadyOrdered, setAlreadyOrdered] = useState(false);
  const [existingOrderNum, setExistingOrderNum] = useState('');
  const [checking, setChecking] = useState(true);

  const isOtherModel = form.phoneModel === '其他（請自行輸入）';
  const finalPhoneModel = isOtherModel ? form.customPhoneModel.trim() : form.phoneModel;

  useEffect(() => {
    // 檢查是否已下過單
    if (donationNum) {
      apiFetch<{ ordered: boolean; orderNum: string | null }>(`/api/public/phone-case-order-check?dn=${encodeURIComponent(donationNum)}`).then((res) => {
        if (res.success && res.data?.ordered) {
          setAlreadyOrdered(true);
          setExistingOrderNum(res.data.orderNum || '');
        }
        setChecking(false);
      });
    } else {
      setChecking(false);
    }

    apiFetch<Illustration[]>('/api/public/ahjim-illustrations').then((res) => {
      if (res.success && res.data) setIllustrations(res.data.filter((i) => i.imageUrl && !i.imageUrl.includes('placeholder')));
    });
  }, [donationNum]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!donationNum || !form.donorName || !finalPhoneModel) return;
    setLoading(true);
    const res = await apiFetch<{ orderNum: string; message: string }>('/api/public/phone-case-order', {
      method: 'POST',
      body: JSON.stringify({
        donationNum,
        donorName: form.donorName,
        donorPhone: '',
        phoneModel: finalPhoneModel,
        illustrationChoice: form.illustrationChoice,
        colorPreference: form.colorPreference,
        sfLockerCode: form.sfLockerCode,
        sfLockerAddress: form.sfLockerAddress,
      }),
    });
    setLoading(false);
    if (res.success && res.data) {
      setOrderNum(res.data.orderNum);
      setSubmitted(true);
    } else {
      alert(res.error?.message || '提交失敗');
    }
  };

  if (checking) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <Loader2 className="w-8 h-8 animate-spin text-brand-600" />
      </div>
    );
  }

  if (alreadyOrdered) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">訂單已提交</h2>
          <p className="text-gray-600 mb-4">你將收到 Whatsapp 確認，預計 7 個工作天寄出。</p>
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-500">訂單編號</p>
            <p className="text-lg font-mono font-bold text-gray-900">{existingOrderNum}</p>
          </div>
          <p className="text-xs text-gray-400">如有問題請 WhatsApp 聯絡我們</p>
        </div>
      </div>
    );
  }

  if (submitted) {
    return (
      <div className="min-h-screen bg-cream flex items-center justify-center p-4">
        <div className="bg-white rounded-3xl shadow-xl p-8 max-w-md w-full text-center">
          <CheckCircle className="w-16 h-16 text-green-500 mx-auto mb-4" />
          <h2 className="text-2xl font-bold text-gray-900 mb-2">訂單已提交</h2>
          <p className="text-gray-600 mb-4">你將收到 Whatsapp 確認，預計 7 個工作天寄出。</p>
          <div className="bg-gray-50 rounded-xl p-4 mb-4">
            <p className="text-sm text-gray-500">訂單編號</p>
            <p className="text-lg font-mono font-bold text-gray-900">{orderNum}</p>
          </div>
          <p className="text-xs text-gray-400">請截圖保存訂單編號以便查詢</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream py-12 px-4">
      <div className="max-w-lg mx-auto">
        <div className="text-center mb-8">
          <Package className="w-12 h-12 text-brand-600 mx-auto mb-3" />
          <h1 className="text-2xl font-bold text-gray-900">手機殼訂單</h1>
          <p className="text-gray-600 text-sm mt-1">
            捐款滿 $500 的專屬禮物 — 任選阿占插畫設計您的專屬手機殼
          </p>
        </div>

        <form onSubmit={handleSubmit} className="bg-white rounded-3xl shadow-lg p-6 space-y-5">
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">捐款編號</label>
            <input type="text" value={donationNum} disabled className="w-full px-4 py-3 rounded-xl border border-gray-200 bg-gray-50 text-gray-500 text-sm" />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">姓名 *</label>
            <input type="text" value={form.donorName} onChange={(e) => setForm({ ...form, donorName: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400" placeholder="您的姓名" required />
          </div>

          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-1">手機型號 *</label>
            <div className="relative">
              <Smartphone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
              <select
                value={form.phoneModel}
                onChange={(e) => setForm({ ...form, phoneModel: e.target.value, customPhoneModel: '' })}
                className="w-full pl-9 pr-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 appearance-none bg-white"
                required
              >
                <option value="">請選擇手機型號</option>
                {PHONE_MODELS.map((m) => (
                  <option key={m} value={m}>{m}</option>
                ))}
              </select>
            </div>
            {isOtherModel && (
              <input
                type="text"
                value={form.customPhoneModel}
                onChange={(e) => setForm({ ...form, customPhoneModel: e.target.value })}
                className="w-full mt-2 px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400"
                placeholder="請輸入您的手機型號"
                required
              />
            )}
          </div>

          {/* Illustration Selection */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">
              喜歡的插圖 *
              <a href="https://jamestyle.com" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-2 text-brand-600 hover:text-brand-700 text-xs font-normal underline">
                JameStyle.com <ExternalLink className="w-3 h-3" />
              </a>
            </label>
            {illustrations.length > 0 ? (
              <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2 max-h-80 overflow-y-auto p-1">
                {illustrations.map((illus, idx) => (
                  <button
                    key={illus.id}
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, illustrationChoice: `№${idx + 1} ${illus.title}` }))}
                    className={`relative rounded-xl overflow-hidden border-2 transition-all aspect-square ${
                      form.illustrationChoice.includes(`№${idx + 1}`)
                        ? 'border-brand-500 ring-2 ring-brand-200'
                        : 'border-gray-200 hover:border-gray-300'
                    }`}
                  >
                    <img src={illus.imageUrl} alt={illus.title} className="w-full h-full object-cover" />
                    <div className="absolute inset-x-0 bottom-0 bg-black/50 text-white text-[10px] py-0.5 text-center truncate px-1">
                      №{idx + 1} {illus.title}
                    </div>
                  </button>
                ))}
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl p-4 text-sm text-gray-400 text-center">
                暫無插圖資料，請直接填寫偏好
              </div>
            )}
            <input type="text" value={form.illustrationChoice} onChange={(e) => setForm({ ...form, illustrationChoice: e.target.value })} className="w-full mt-2 px-4 py-2 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400" placeholder="已選 / 手動輸入插圖編號..." />
          </div>

          {/* Color Preference */}
          <div>
            <label className="block text-sm font-semibold text-gray-700 mb-2">顏色偏好 *</label>
            <div className="mb-2">
              <a href="https://s2c-donation-api.jimsbond007.workers.dev/api/public/media/phone-case-colors.jpg" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-700 underline">
                <ExternalLink className="w-3 h-3" /> 查看顏色參考圖
              </a>
            </div>
            <div className="grid grid-cols-5 gap-2">
              {COLOR_OPTIONS.map((c) => (
                <button
                  key={c.value}
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, colorPreference: c.value }))}
                  className={`px-2 py-2.5 rounded-xl text-xs font-medium border transition-all ${
                    form.colorPreference === c.value
                      ? 'bg-brand-50 border-brand-400 text-brand-700'
                      : 'bg-white border-gray-200 text-gray-600 hover:border-gray-300'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-gray-100 pt-4">
            <label className="block text-sm font-semibold text-gray-700 mb-1 flex items-center gap-1">
              <MapPin className="w-4 h-4" /> 順豐櫃到付 *
              <a href="https://hk.sf-express.com/hk/tc/more/sf-store-address" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 ml-2 text-brand-600 hover:text-brand-700 text-xs font-normal underline">
                查找順豐櫃 <ExternalLink className="w-3 h-3" />
              </a>
            </label>
            <input type="text" value={form.sfLockerCode} onChange={(e) => setForm({ ...form, sfLockerCode: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400 mb-2" placeholder="順豐櫃編號（如：852H101）" required />
            <input type="text" value={form.sfLockerAddress} onChange={(e) => setForm({ ...form, sfLockerAddress: e.target.value })} className="w-full px-4 py-3 rounded-xl border border-gray-200 text-sm outline-none focus:border-brand-400" placeholder="備註" />
          </div>

          <button type="submit" disabled={loading} className="w-full flex items-center justify-center gap-2 btn-primary py-3.5 disabled:opacity-50">
            {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : <Send className="w-5 h-5" />}
            提交訂單
          </button>
        </form>
      </div>
    </div>
  );
}
